import { ViewportScroller } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnInit,
    Renderer2,
    TemplateRef,
    input,
    inject,
    output,
    signal,
    OnDestroy,
} from '@angular/core';
import { UNDERCONSTRUCTION_TITLE } from '@bazis/configuration.service';

export interface TabItem {
    id: string | number;
    title?: string;
    titleKey?: string;
    titleParams?: any;
    entityType?: string;
    routerLink?: string;
    isExactLink?: boolean;
    disabled?: boolean;
    // name иконки или src
    hasBadge?: boolean;
    icon?: string;
    iconPosition?: 'start' | 'end';
    underconstruction?: boolean;
    hidden?: boolean;
}

// возможно надо будет предусмотреть для моб. устройств логику слайда табов в строке, когда они не помещаются в строку.
// еще лучше если для свайпа под мобильные сделать директиву. Так как может понадобится во многих компонентах

const sharedStylesheet = new CSSStyleSheet();
sharedStylesheet.replaceSync(
    `:host { --height: initial; --padding-start: 0; --padding-end: 0; --padding-top: 0; --padding-bottom: 0; --border-radius: initial; --border-width: initial; --border-style: initial; --border-color: initial; --tab-border-color-focused: var(--tab-border-color); --tab-border-color-hover: var(--tab-border-color); --tab-border-color-selected: var(--tab-border-color); --tab-border-color-disabled: var(--tab-border-color); --background: initial; --box-shadow: none; --color: inherit; --color-focused: var(--color); --color-hover: var(--color); --color-selected: var(--color); --color-disabled: var(--color); --font-size: initial; --font-weight: initial; --line-height: initial; --font-size-selected: var(--font-size); --font-weight-selected: var(--font-weight); --line-height-selected: var(--line-height); --font-size-disabled: var(--font-size); --font-weight-disabled: var(--font-weight); --line-height-disabled: var(--line-height); --dropdown-min-width: initial; --dropdown-top: var(--height); --dropdown-z-index: 100; --dropdown-padding-start: 0; --dropdown-padding-end: 0; --dropdown-padding-top: 0; --dropdown-padding-bottom: 0; --dropdown-background: initial; --dropdown-border-radius: initial; --dropdown-border-width: initial; --dropdown-border-style: initial; --dropdown-border-color: initial; --dropdown-box-shadow: initial; display: block; } :host .bazis-tabs-wrapper { display: flex; align-items: center; justify-content: flex-start; gap: var(--tabs-gap, var(--tab-margin-end)); width: auto; height: var(--height); padding-left: var(--padding-start); padding-right: var(--padding-end); padding-top: var(--padding-top); padding-bottom: var(--padding-bottom); border-radius: var(--border-radius); border-width: var(--border-width); border-style: var(--border-style); border-color: var(--border-color); background: var(--background); box-shadow: var(--box-shadow); color: var(--color); text-align: center; user-select: none; z-index: 10; } :host .bazis-tab_more { position: relative; height: 100%; } :host .bazis-tab_more > .bazis-tab-btn { text-transform: capitalize; } :host .bazis-tab__dropdown { position: absolute; top: var(--dropdown-top); right: 0; z-index: var(--dropdown-z-index); min-width: var(--dropdown-min-width); padding-left: var(--dropdown-padding-start); padding-right: var(--dropdown-padding-end); padding-top: var(--dropdown-padding-top); padding-bottom: var(--dropdown-padding-bottom); background: var(--dropdown-background); border-radius: var(--dropdown-border-radius); border-width: var(--dropdown-border-width); border-style: var(--dropdown-border-style); border-color: var(--dropdown-border-color); box-shadow: var(--dropdown-box-shadow); } :host .bazis-tab__dropdown .bazis-tab-btn { width: 100%; justify-content: flex-start; } @supports (margin-inline-start: 0) or (-webkit-margin-start: 0) { .bazis-tabs-wrapper { padding-left: unset; padding-right: unset; -webkit-padding-start: var(--padding-start); padding-inline-start: var(--padding-start); -webkit-padding-end: var(--padding-end); padding-inline-end: var(--padding-end); } } :host(.bazis-color) .bazis-tab-btn { --tab-border-color-focused: var(--bazis-color-shade); --color-selected: var(--bazis-color-contrast); } :host(.bazis-color) .bazis-tab-btn--focused, :host(.bazis-color) .bazis-tab-btn :focus { background: var(--tab-background-focused); } :host(.bazis-color) .bazis-tab-btn--selected { color: var(--bazis-color-contrast); } :host(.bazis-color) .bazis-tabs-wrapper, :host(.bazis-color) .bazis-tab-btn { background: var(--bazis-color-base); color: rgba(var(--bazis-color-contrast-rgb), 0.7); } :host(.bazis-tabs_scroll) { overflow: hidden; } :host(.bazis-tabs_scroll) .bazis-tabs-wrapper { overflow-x: auto; scrollbar-width: none; scroll-snap-type: x mandatory; } :host(.bazis-tabs_scroll) .bazis-tabs-wrapper .bazis-tab-btn { scroll-snap-align: start; }`,
);

@Component({
    selector: 'bazis-tabs',
    templateUrl: './tabs.component.html',
    // styleUrls: ['tabs.component.scss'],
    // encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TabsElement implements OnInit, OnDestroy {
    public el = inject(ElementRef);

    private viewportScroller = inject(ViewportScroller);

    private renderer = inject(Renderer2);

    readonly tabList = input<TabItem[]>();

    readonly selectedTab = input<string | number>();

    readonly hideByZeroCount = input(false);

    readonly needScroll = input(true);

    @Input() tabTemplate: TemplateRef<any>;

    readonly hasMore = input(false);

    readonly moreInsideItemsCounter = input(0);

    readonly underconstructionKey = input(UNDERCONSTRUCTION_TITLE.section);

    readonly adaptiveMode = input<'scroll' | 'useMore'>('scroll');

    readonly downIcon = input('angle-small-down');

    readonly upIcon = input('angle-small-up');

    selectedTabChange = output<string | number>();

    removeClickListener: Function;

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement) {
        const clickedOutside = targetElement !== this.el.nativeElement;
        if (clickedOutside) {
            this.showMore.set(false);
        }
    }

    @HostBinding('class') get tabsClass() {
        return {
            [`bazis-tabs_${this.adaptiveMode()}`]: !!this.adaptiveMode(),
        };
    }

    showMore = signal(false);

    ngOnDestroy() {
        if (this.removeClickListener) this.removeClickListener();
    }

    ngOnInit() {
        this.removeClickListener = this.renderer.listen(this.el.nativeElement, 'click', (event) => {
            this.showMore.set(false);
        });
    }

    selectTab(tab: string | number) {
        // console.log('selectTab', this.selectedTab, tab);
        this.selectedTabChange.emit(tab);

        if (!this.needScroll()) return;
        // setTimeout необходим, чтобы не приходилось кликать дважды на кнопку, если передано Output значение
        // данный баг двойного клика наблюдается в FF MAC OS
        setTimeout(() => this.viewportScroller.scrollToPosition([0, 0]), 0);
    }

    swipeLeft(event) {
        // console.log('swipe left', event);
    }

    swipeRight(event) {
        // console.log('swipe right', event);
    }

    toggleMoreTab(event) {
        event.stopPropagation();
        event.preventDefault();
        this.showMore.set(!this.showMore());
    }
}
